/**
 * Notifications API client (MV2-217 / S5).
 *
 * The bell + /notifications page surface — read API + dismiss + undo +
 * mute + ticket watchers. Backend lives at
 * `monitoring-backend/app/routers/notifications.py` (read-API was
 * extended in MV2-215 / PR #228; dispatcher writes the rows in
 * MV2-214 / PR #227).
 *
 * Every queryKey includes ``actingAsClientId`` per CROSS_PAGE §13 so
 * the bell flips atomically on SuperAdmin impersonation enter/exit
 * — no ghost badges from the previous tenant.
 *
 * Polling cadences (spec §4.3):
 *   - Header badge        →  30 s
 *   - Popover (open)      →  30 s on top of the on-open refetch
 *   - Full /notifications →  60 s while visible (browser pauses
 *                            React Query refetchInterval when the
 *                            tab is backgrounded — no extra wiring)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { useAuthStore } from "@/store/authStore";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NotificationCategory =
  | "mention"
  | "ticket"
  | "alarm"
  | "report"
  // MV2-470: CSV export-ready notifications. Surfaced alongside reports
  // under the "Reports & exports" tab.
  | "export"
  | "system"
  | "account";

export type NotificationTab =
  | "all"
  | "unread"
  | "mentions"
  | "tickets"
  | "alarms"
  | "reports"
  | "system";

export interface NotificationRow {
  id: string;
  template_key: string;
  subject: string | null;
  body_preview: string | null;
  channel: string;
  status: string;
  related_type: string | null;
  related_id: string | null;
  created_at: string;
  read_at: string | null;
  category: string;
  event: string;
  title: string;
  snippet: string | null;
  site_id: string | null;
  /** MV2-735: resolved server-side per page. The row always carried
   * ``site_id``, but a bare UUID gave the feed nothing to render, so a
   * device-level alarm never said which site it was on. */
  site_name: string | null;
  dismissed_at: string | null;
  /** Producer-stamped denormalised ids (ticket_id / comment_id /
   * actor_user_id …). Used by ``rowHref()`` to build deep-link URLs
   * — for ``ticket_comment`` rows the ``related_id`` is the comment
   * id, not the ticket id, so the route needs ``payload.ticket_id``
   * to land on the right page. */
  payload: Record<string, unknown> | null;
}

export interface NotificationListResponse {
  items: NotificationRow[];
  next_cursor: string | null;
}

export interface UnreadCountByCategory {
  mentions: number;
  tickets: number;
  alarms: number;
  system: number;
  account: number;
  report: number;
}

export interface UnreadCountResponse {
  /** Capped at 999. */
  unread: number;
  by_category: UnreadCountByCategory;
  /** MV2-878 (backend.md §47): all non-dismissed rows, read or not, uncapped — "12 unread of 212". */
  total?: number;
  /** MV2-878: totals keyed on the raw `category` values (mention · ticket · alarm · report · export · system · account). */
  by_category_total?: Record<string, number>;
}

export interface FeedFilters {
  tab?: NotificationTab;
  q?: string;
  from?: string;
  to?: string;
  site_id?: string[];
  category?: NotificationCategory[];
  include_dismissed?: boolean;
  cursor?: string;
  limit?: number;
}

export interface MuteRow {
  category: string;
  until_ts: string;
}

export interface WatcherRow {
  user_id: string;
  display_name: string;
  watched_at: string;
}

export interface WatcherList {
  watchers: WatcherRow[];
  me_watching: boolean;
  count: number;
}

// ---------------------------------------------------------------------------
// Feed + unread count
// ---------------------------------------------------------------------------

/** Build the query string for /me/notifications/feed.
 * Stable serialisation keeps the queryKey hashable + cache-friendly. */
function buildFeedParams(filters: FeedFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.tab && filters.tab !== "all") params.set("tab", filters.tab);
  if (filters.q) params.set("q", filters.q);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.site_id)
    for (const id of filters.site_id) params.append("site_id", id);
  if (filters.category)
    for (const c of filters.category) params.append("category", c);
  if (filters.include_dismissed) params.set("include_dismissed", "true");
  if (filters.cursor) params.set("cursor", filters.cursor);
  if (filters.limit) params.set("limit", String(filters.limit));
  return params;
}

/**
 * Bell-feed query. Pass ``filters.limit = 8`` for the popover preview;
 * the full page uses ``limit = 50`` + cursor pagination.
 *
 * Polling cadence is the caller's responsibility — bell badges and
 * pop-overs share the same hook but at different cadences. The page
 * passes ``refetchInterval = 60_000``; the popover passes 30 000.
 */
export function useNotificationsFeed(
  filters: FeedFilters = {},
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<NotificationListResponse>({
    queryKey: [
      "notifications",
      "feed",
      actingAsClientId,
      filters.tab ?? "all",
      filters.q ?? "",
      filters.from ?? null,
      filters.to ?? null,
      filters.site_id ?? null,
      filters.category ?? null,
      filters.include_dismissed ?? false,
      filters.cursor ?? null,
      filters.limit ?? 50,
    ],
    queryFn: async () => {
      const params = buildFeedParams(filters);
      const qs = params.toString();
      return (
        await apiClient.get<NotificationListResponse>(
          `/me/notifications/feed${qs ? `?${qs}` : ""}`,
        )
      ).data;
    },
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval,
  });
}

/**
 * Unread-count badge poll. 30 s cadence, paused when the tab is
 * backgrounded (React Query default). Returns total + by_category
 * breakdown for the popover chip row.
 */
export function useUnreadCount() {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<UnreadCountResponse>({
    queryKey: ["notifications", "unread-count", actingAsClientId],
    queryFn: async () =>
      (
        await apiClient.get<UnreadCountResponse>(
          "/me/notifications/unread-count",
        )
      ).data,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    // The global QueryClient sets refetchOnWindowFocus: false so we
    // don't thunder-herd the API on tab-switch — but the bell badge
    // is the one query where users genuinely expect an instant
    // update when they return to the tab. Without this they wait up
    // to 30 s for the next tick, which feels like the badge is
    // stuck. Same reason for refetchOnReconnect.
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
  });
}

// ---------------------------------------------------------------------------
// Mark read
// ---------------------------------------------------------------------------

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation<{ marked: number }, Error, { ids?: string[]; all?: true }>(
    {
      mutationFn: async (body) =>
        (
          await apiClient.post<{ marked: number }>(
            "/me/notifications/mark-read",
            body,
          )
        ).data,
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["notifications"] });
      },
    },
  );
}

// ---------------------------------------------------------------------------
// Dismiss
// ---------------------------------------------------------------------------

export function useDismissOne() {
  const qc = useQueryClient();
  return useMutation<NotificationRow, Error, string>({
    mutationFn: async (id) =>
      (await apiClient.post<NotificationRow>(`/me/notifications/${id}/dismiss`))
        .data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useBulkDismiss() {
  const qc = useQueryClient();
  return useMutation<
    { dismissed: number },
    Error,
    {
      ids?: string[];
      filter?: { tab?: NotificationTab; category?: NotificationCategory[] };
    }
  >({
    mutationFn: async (body) =>
      (
        await apiClient.post<{ dismissed: number }>(
          "/me/notifications/dismiss",
          body,
        )
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Undo (5-second window for optimistic mark-read / dismiss)
// ---------------------------------------------------------------------------

export function useUndo() {
  const qc = useQueryClient();
  return useMutation<
    NotificationRow,
    Error,
    { id: string; action: "mark_read" | "dismiss" }
  >({
    mutationFn: async ({ id, action }) =>
      (
        await apiClient.post<NotificationRow>(`/me/notifications/${id}/undo`, {
          action,
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutes
// ---------------------------------------------------------------------------

export function useMutes() {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<{ mutes: MuteRow[] }>({
    queryKey: ["notifications", "mutes", actingAsClientId],
    queryFn: async () =>
      (await apiClient.get<{ mutes: MuteRow[] }>("/me/notifications/mutes"))
        .data,
  });
}

export function useMuteCategory() {
  const qc = useQueryClient();
  return useMutation<MuteRow, Error, { category: string; until_ts?: string }>({
    mutationFn: async (body) =>
      (await apiClient.post<MuteRow>("/me/notifications/mute-category", body))
        .data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications", "mutes"] });
    },
  });
}

export function useUnmute() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (category) => {
      await apiClient.delete(`/me/notifications/mutes/${category}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications", "mutes"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Ticket watchers (S2 backend, S4 UI)
// ---------------------------------------------------------------------------

export function useTicketWatchers(ticketId: string | undefined) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<WatcherList>({
    queryKey: ["ticket", "watchers", actingAsClientId, ticketId],
    queryFn: async () =>
      (await apiClient.get<WatcherList>(`/tickets/${ticketId}/watchers`)).data,
    enabled: Boolean(ticketId),
  });
}

export function useToggleWatch(ticketId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, "watch" | "unwatch">({
    mutationFn: async (action) => {
      if (!ticketId) throw new Error("ticketId is required");
      if (action === "watch") {
        await apiClient.post(`/tickets/${ticketId}/watch`);
      } else {
        await apiClient.delete(`/tickets/${ticketId}/watch`);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["ticket", "watchers", undefined, ticketId],
      });
      // Also invalidate without the actingAs prefix in case of cache mismatch.
      void qc.invalidateQueries({ queryKey: ["ticket", "watchers"] });
    },
  });
}
